import type {
  Agenda,
  Day,
  Session as ApiSession,
  Speaker as ApiSpeaker,
  Attachment as ApiAttachment
} from '../../../../types/agenda.types';

import type {
  ScheduleSession,
  ScheduleDay,
  Speaker,
  Attachment,
  Survey,
  SessionTypeMap
} from '../../../../types/schedule.types';

// Session type mapping
const SESSION_TYPE_MAP: SessionTypeMap = {
  'keynote': 'keynote',
  'workshop': 'workshop',
  'breakout': 'breakout',
  'panel': 'panel',
  'networking': 'networking',
  'session': 'session', // default mapping
  'plenary': 'keynote',
  'break': 'networking',
  'lunch': 'networking',
  'reception': 'networking'
};

// Transform API speaker to component speaker
export const transformSpeaker = (apiSpeaker: ApiSpeaker): Speaker => {
  // Map speaker type to role
  const mapSpeakerTypeToRole = (type?: string): Speaker['role'] | undefined => {
    if (!type) return undefined;

    const typeMap: Record<string, Speaker['role']> = {
      'Moderator': 'moderator',
      'Panelist': 'panelist',
      'Keynote': 'keynote',
      'Presenter': 'presenter',
      'Host': 'host',
      'Judge': 'presenter', // Map Judge to presenter
      'Sponsor': 'presenter', // Map Sponsor to presenter
      'Speaker': 'presenter' // Default speaker maps to presenter
    };

    return typeMap[type] || undefined;
  };

  return {
    id: apiSpeaker.id,
    name: apiSpeaker.fullName,
    title: apiSpeaker.title || '',
    company: apiSpeaker.company || '',
    image: apiSpeaker.profileImageUrl || '',
    role: mapSpeakerTypeToRole(apiSpeaker.speakerType),
    type: apiSpeaker.speakerType, // Preserve original speaker type
    position: apiSpeaker.position // Preserve position for ordering
  };
};

// Transform API attachment to component attachment
export const transformAttachment = (apiAttachment: ApiAttachment): Attachment => {
  // Determine file type from extension or content type
  const getFileType = (attachment: ApiAttachment): Attachment['type'] => {
    // Get extension from various possible sources
    const ext = attachment.fileExtension?.toLowerCase() ||
                attachment.name?.split('.').pop()?.toLowerCase() ||
                attachment.fileFileName?.split('.').pop()?.toLowerCase();

    // V1 API uses 'type' field for content type (e.g., 'application/pdf')
    // V2 API uses 'fileContentType' field
    const contentType = (attachment.fileContentType || (attachment as any).type)?.toLowerCase();

    if (ext === 'pdf' || contentType?.includes('pdf')) return 'pdf';
    if (ext === 'ppt' || ext === 'pptx' || contentType?.includes('powerpoint') || contentType?.includes('presentation')) return 'ppt';
    if (ext === 'doc' || ext === 'docx' || contentType?.includes('word') || contentType?.includes('document')) return 'doc';
    if (ext === 'xls' || ext === 'xlsx' || contentType?.includes('excel') || contentType?.includes('spreadsheet')) return 'xls';
    if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || contentType?.includes('image')) return 'image';
    if (ext === 'zip' || ext === 'rar' || contentType?.includes('zip') || contentType?.includes('compressed')) return 'zip';
    return 'other';
  };

  return {
    id: apiAttachment.id,
    name: apiAttachment.name || apiAttachment.fileFileName || 'Download',
    type: getFileType(apiAttachment),
    // V1 API uses 'url' or 'fileUrl', V2 API uses 'downloadUrl'
    url: apiAttachment.url || apiAttachment.downloadUrl || apiAttachment.fileUrl || (apiAttachment as any).file_url || '#',
    // V1 API uses 'file_file_size', V2 uses 'fileFileSize'
    size: apiAttachment.fileSizeFormatted ||
          (apiAttachment.fileFileSize ? `${Math.round(apiAttachment.fileFileSize / 1024)} KB` :
           (apiAttachment as any).file_file_size ? `${Math.round((apiAttachment as any).file_file_size / 1024)} KB` : '')
  };
};

// Transform session type
export const transformSessionType = (apiType: string): ScheduleSession['type'] => {
  const normalizedType = apiType?.toLowerCase() || 'session';
  return SESSION_TYPE_MAP[normalizedType] || '';
};

// Format time from ISO string
export const formatTime = (isoTime: string | null): string => {
  if (!isoTime) return '';

  try {
    // Extract just the time portion to avoid timezone conversion issues
    // Expected format: "2000-01-01T08:00:00.000Z"
    const timeMatch = isoTime.match(/T(\d{2}):(\d{2})/);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];

      // Adjust for UTC (subtract timezone offset if the time includes Z)
      if (isoTime.endsWith('Z')) {
        // Time is in UTC, so use it directly
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${minutes} ${period}`;
      }
    }

    // Fallback to date parsing if pattern doesn't match
    const date = new Date(isoTime);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC' // Force UTC to prevent timezone conversion
    });
  } catch {
    return '';
  }
};

// Calculate duration in minutes
export const calculateDuration = (startTime: string | null, endTime: string | null): number | null => {
  if (!startTime || !endTime) return null;

  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return Math.round((end.getTime() - start.getTime()) / 60000);
  } catch {
    return null;
  }
};

// Transform API session to component session
export const transformSession = (apiSession: ApiSession): ScheduleSession => {
  // Ensure speakers is an array
  const speakersArray = Array.isArray(apiSession.speakers) ? apiSession.speakers : [];

  // Handle speakers that may be in JSON:API format or direct format
  const speakers = speakersArray.map((speaker: any) => {
    // If speaker has full_name property (snake case), convert to camelCase
    if (speaker.full_name) {
      return transformSpeaker({
        id: speaker.id,
        fullName: speaker.full_name,
        firstName: speaker.first_name,
        lastName: speaker.last_name,
        bio: speaker.bio,
        company: speaker.company,
        title: speaker.title,
        profileImageUrl: speaker.profile_image_url || speaker.image_url, // V1 uses image_url
        linkedinUrl: speaker.linkedin_url,
        twitterHandle: speaker.twitter_handle,
        speakerType: speaker.speaker_type,
        position: speaker.position
      });
    }
    // Handle V1 format where fields might have different names
    if (speaker.name && !speaker.fullName) {
      return transformSpeaker({
        id: speaker.id,
        fullName: speaker.name,
        firstName: speaker.name.split(' ')[0],
        lastName: speaker.name.split(' ').slice(1).join(' '),
        bio: speaker.bio,
        company: speaker.company,
        title: speaker.title,
        profileImageUrl: speaker.profileImageUrl || speaker.imageUrl || speaker.image_url || speaker.image,
        linkedinUrl: speaker.linkedinUrl,
        twitterHandle: speaker.twitterHandle,
        speakerType: speaker.type || speaker.speakerType || speaker.speaker_type,
        position: speaker.position
      });
    }
    // Otherwise it's already in the expected camelCase format
    return transformSpeaker(speaker);
  }).sort((a, b) => (a.position || 999) - (b.position || 999)); // Sort by position

  // If no roles were set from the API, apply defaults based on session type
  const hasRoles = speakers.some(s => s.role !== undefined);
  if (!hasRoles) {
    if (apiSession.sessionType === 'panel' && speakers.length > 1) {
      // First speaker is moderator, rest are panelists
      speakers[0].role = 'moderator';
      speakers.slice(1).forEach(s => s.role = 'panelist');
    } else if (apiSession.sessionType === 'keynote' && speakers.length > 0) {
      speakers.forEach(s => s.role = 'keynote');
    } else {
      speakers.forEach(s => s.role = 'presenter');
    }
  }

  // Create survey object from the actual survey relationship data
  const survey: Survey | undefined = apiSession.survey ? {
    id: apiSession.survey.id,
    title: apiSession.survey.name || apiSession.survey.title || `${apiSession.name} Feedback`,
    url: apiSession.survey.url || (apiSession.survey.slug ? `/surveys/${apiSession.survey.slug}` : '#'),
    isCompleted: apiSession.survey.isCompleted || false,
    canBeTaken: apiSession.survey.canBeTaken
  } : undefined;

  // Use session image if available, otherwise fall back to first speaker's image
  const sessionImage = apiSession.image;

  // Extract individual start and end times from formattedTime or use formatTime
  let startTime: string;
  let endTime: string;

  if (apiSession.formattedTime) {
    // Parse "8:00 AM - 9:00 AM" format
    const timeParts = apiSession.formattedTime.split(' - ');
    startTime = timeParts[0] || formatTime(apiSession.startTime);
    endTime = timeParts[1] || formatTime(apiSession.endTime);
  } else {
    startTime = formatTime(apiSession.startTime);
    endTime = formatTime(apiSession.endTime);
  }

  return {
    id: apiSession.id,
    title: apiSession.name,
    description: apiSession.description || '',
    speakers,
    time: startTime,
    endTime: endTime,
    type: transformSessionType(apiSession.sessionType),
    track: undefined,
    room: apiSession.room || undefined,
    durationMinutes: apiSession.durationMinutes || undefined,
    capacity: apiSession.capacity || undefined,
    registeredCount: apiSession.registrationsCount,
    isBreakout: apiSession.sessionType === 'breakout' && !apiSession.isParentSession,
    breakoutGroup: undefined, // Will be set by parent when processing breakouts
    image: sessionImage,
    attachments: apiSession.attachments?.map(transformAttachment),
    survey,
    isLivestream: apiSession.hasLivestream || !!apiSession.videoUrl,
    livestreamId: apiSession.livestreamId || undefined,
    livestreamUrl: apiSession.videoUrl || undefined,
    sponsors: [], // Would need to be populated from event or session sponsor data
    categories: apiSession.universalCategories || []
  };
};

// Transform API day to component schedule day
export const transformDay = (apiDay: Day): ScheduleDay => {
  // First, transform all sessions
  const allSessions = apiDay.sessions.map(transformSession);

  // Then, collect all breakout sessions from parent sessions
  const breakoutSessions: ScheduleSession[] = [];

  apiDay.sessions.forEach(apiSession => {
    if (apiSession.isParentSession && apiSession.breakoutSessions && apiSession.breakoutSessions.length > 0) {
      // Transform each breakout session and mark it as a breakout
      const transformedBreakouts = apiSession.breakoutSessions.map(breakoutSession => {
        const transformed = transformSession(breakoutSession);
        // Ensure it's marked as a breakout and set the parent group name
        transformed.isBreakout = true;
        transformed.breakoutGroup = apiSession.name;
        return transformed;
      });
      breakoutSessions.push(...transformedBreakouts);
    }
  });

  // Combine all sessions and sort by time
  const sessions = [...allSessions, ...breakoutSessions]
    .sort((a, b) => {
      // Sort by time
      const parseTime = (timeStr: string): number => {
        const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!match) return 0;

        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const isPM = match[3].toUpperCase() === 'PM';

        if (isPM && hours !== 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;

        return hours * 60 + minutes;
      };

      return parseTime(a.time) - parseTime(b.time);
    });

  // Extract just the day name if it contains a full date
  let dayTitle = apiDay.name || `Day ${apiDay.dayOfWeek}`;

  // Check if the name contains a comma (like "Thursday, September 25")
  // If so, extract just the day of week
  if (dayTitle.includes(',')) {
    const parts = dayTitle.split(',');
    dayTitle = parts[0].trim();
  }

  // Get the subtitle
  let subtitle = apiDay.formattedDate || formatDaySubtitle(apiDay.date);

  // If the subtitle starts with the same day name as the title, remove the redundant day name
  // e.g., if title is "Thursday" and subtitle is "Thursday, September 25",
  // change subtitle to just "September 25"
  if (subtitle.startsWith(dayTitle + ',')) {
    subtitle = subtitle.substring(dayTitle.length + 1).trim();
  }

  return {
    id: apiDay.id, // Include the day ID for unique identification
    date: apiDay.date.split('T')[0], // Ensure YYYY-MM-DD format
    title: dayTitle,
    subtitle: subtitle,
    sessions
  };
};

// Format day subtitle
const formatDaySubtitle = (dateString: string): string => {
  try {
    // Parse the date string to avoid timezone conversion issues
    // Expecting format: YYYY-MM-DD
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);

    // Create date using UTC to avoid timezone shifts
    const date = new Date(Date.UTC(year, month - 1, day));

    // Format using UTC methods to maintain the exact date
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
    const monthName = date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    const dayNum = date.getUTCDate();

    return `${weekday}, ${monthName} ${dayNum}`;
  } catch {
    return dateString;
  }
};

// Transform agenda to schedule data
export const transformAgendaToScheduleData = (agenda: Agenda): ScheduleDay[] => {
  // Create a modified transform function that includes event slug
  const transformDayWithEventSlug = (apiDay: Day): ScheduleDay => {
    // Transform the day, but pass the event slug to sessions
    const day = transformDay(apiDay);

    // Update session URLs with the agenda's event slug if available
    if (agenda.eventSlug) {
      day.sessions = day.sessions.map(session => ({
        ...session,
        survey: session.survey ? {
          ...session.survey,
          url: session.survey.url === '#' && agenda.eventSlug
            ? `/events/${agenda.eventSlug}/surveys/${session.survey.id}`
            : session.survey.url
        } : undefined
      }));
    }

    return day;
  };

  return agenda.days.map(transformDayWithEventSlug);
};


// Generate calendar URL (same as mockData implementation)
export const generateCalendarUrl = (session: ScheduleSession, calendarType: 'google' | 'outlook' | 'apple', eventDate: string) => {
  const startDate = new Date(`${eventDate} ${session.time}`);
  const endDate = new Date(`${eventDate} ${session.endTime}`);

  const formatGoogleDate = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '');
  };

  const formatICSDate = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, -1);
  };

  const title = encodeURIComponent(session.title);
  const location = encodeURIComponent(session.room || 'Event Location');
  const details = encodeURIComponent(
    `${session.description}\n\nSpeakers: ${session.speakers.map(s => s.name).join(', ')}`
  );

  switch (calendarType) {
    case 'google':
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${details}&location=${location}&sf=true&output=xml`;

    case 'outlook':
      return `https://outlook.office.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&location=${location}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}`;

    case 'apple':
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `DTSTART:${formatICSDate(startDate)}`,
        `DTEND:${formatICSDate(endDate)}`,
        `SUMMARY:${session.title}`,
        `DESCRIPTION:${session.description}`,
        `LOCATION:${session.room || 'Event Location'}`,
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\n');

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      return URL.createObjectURL(blob);

    default:
      return '#';
  }
};